import isEmpty from 'lodash-es/isEmpty';
import omit from 'lodash-es/omit';
import { fromEvent, Observable } from 'rxjs';
import { share, tap } from 'rxjs/operators';
import { isEmptyIterable } from '../../helpers/iterable';
import { eventHasMetaKeyForPlatform } from '../../helpers/platform/modifierKeyHelpers';
import { applyStyleToRange } from '../../helpers/style/applyStyleToRange';
import { getStyleFromRange } from '../../helpers/style/getStyleFromRange';
import { removeStyleFromRange } from '../../helpers/style/removeStyleFromRange';
import {
  applyStyleToElement,
  BooleanStylePropertyName,
  DEFAULT_STYLE,
  getStyleFromElements,
  Style,
} from '../../helpers/style/style';
import { InteractionAction, InteractionHandler, InteractionTrigger } from '../../interactionEvent';
import { createConnectingService } from '../base/connectingService';
import { ElementSelectionService } from '../dom/elementSelectionService';
import { ElementService } from '../dom/elementService';
import { NULL_STYLE_SERVICE, StyleService } from './styleService';

const STYLE_SHORTCUTS: { [key: string]: StyleShortcutConfig } = {
  b: { styleKey: 'isBold', action: InteractionAction.TOGGLE_BOLD },
  i: { styleKey: 'isItalic', action: InteractionAction.TOGGLE_ITALIC },
  u: { styleKey: 'hasUnderline', action: InteractionAction.TOGGLE_UNDERLINE },
};

type StyleShortcutConfig = {
  styleKey: BooleanStylePropertyName;
  action: InteractionAction;
};

export type ElementStyleServiceOptions = {
  isUnderlineDisabled?: boolean;
  areKeyboardShortcutsDisabled?: boolean;
  onInteraction?: InteractionHandler;
};

export function createElementStyleService(
  element$: Observable<Element | null>,
  selectionService: ElementSelectionService,
  options: ElementStyleServiceOptions = {},
): StyleService {
  return createConnectingService(
    element$,
    () => NULL_STYLE_SERVICE,
    element => new ElementStyleService(element, selectionService, options),
  );
}

export class ElementStyleService extends ElementService<Element> implements StyleService {
  constructor(
    element: Element,
    selectionService: ElementSelectionService,
    options: ElementStyleServiceOptions = {},
  ) {
    super(element, selectionService, {
      effects$: fromEvent<KeyboardEvent>(element, 'keydown').pipe(
        tap(event => {
          // Note: we've seen cases of event.key being undefined.
          const key = event.key ? event.key.toLowerCase() : '';

          // TODO: Use appropriate modifier for the current platform.
          if (eventHasMetaKeyForPlatform(event) && key in STYLE_SHORTCUTS) {
            // Disable the default browser's behavior no matter what.
            // TODO: Reduce duplication with createContentEditableResetService().
            event.preventDefault();

            if (!options.areKeyboardShortcutsDisabled) {
              const { style } = this;
              const { styleKey, action } = STYLE_SHORTCUTS[key];

              this.applyStyle({
                [styleKey]: !style[styleKey],
              });

              if (options.onInteraction) {
                options.onInteraction({
                  trigger: InteractionTrigger.KEYBOARD_SHORTCUT,
                  action,
                });
              }
            }
          }
        }),
        share(),
      ),
    });

    this.selectionService = selectionService;
    this.isUnderlineDisabled = Boolean(options.isUnderlineDisabled);
  }

  private readonly selectionService: ElementSelectionService;
  private readonly isUnderlineDisabled: boolean;

  get style() {
    const { selection, extraSelectedElements } = this.selectionService;
    if (!selection) {
      return {};
    }

    const { element } = this;

    if (!isEmptyIterable(extraSelectedElements)) {
      return getStyleFromElements(element, extraSelectedElements) ?? DEFAULT_STYLE;
    }

    const { range } = selection;
    return getStyleFromRange(element, range);
  }

  applyStyle(style: Style) {
    if (isEmpty(style)) {
      return;
    }

    const { selection, extraSelectedElements } = this.selectionService;
    if (!selection) {
      return;
    }

    if (!isEmptyIterable(extraSelectedElements)) {
      for (const element of extraSelectedElements) {
        applyStyleToElement(element as HTMLElement, style);
      }
      return;
    }

    const { element } = this;
    const { isBackward } = selection;
    let { range } = selection;

    range = applyStyleToRange(element, range, this.maybeDisableUnderline(style));
    this.selectionService.selectWithRange(range, isBackward);
  }

  private maybeDisableUnderline(style: Style): Style {
    if (!this.isUnderlineDisabled) {
      return style;
    }

    return omit(style, 'hasUnderline');
  }

  resetStyle() {
    const { selection } = this.selectionService;
    if (!selection) {
      return;
    }

    const { element } = this;
    const { isBackward } = selection;
    let { range } = selection;

    range = removeStyleFromRange(element, range);
    this.selectionService.selectWithRange(range, isBackward);
  }
}
